import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations } from '@/ts/store/Constants';
import Breakpoints from 'breakpoints-js';
import { ViewOptions } from '@/ts/HococoInterfaces';
import ErrorResponse from '@/ts/ErrorResponse';

@Module({name: 'Viewport', namespaced: true})
export default class Viewport extends VuexModule {
    public isRequireAutoHeight: boolean = false;
    public width: number = 0;
    public height: number = 0;
    public deviceWidth: number = 0;
    public deviceHeight: number = 0;
    public isMobile: boolean = false;
    public isDesktop: boolean = false;
    public isTablet: boolean = false;
    public isLandscape: boolean = false;
    public initalized: boolean = false;
    public isShelfVisible: boolean = false;
    public headerTextContent: string = '';
    public rightHeaderButtonKey: string = '';
    public leftHeaderButtonKey: string = '';
    public view: string = '';
    public viewMode: string = '';
    public transparentHeader = false;
    public animateTransparentHeader = false;
    public searchFieldVisible: boolean = false;
    public deviceInfo: any = null;
    public bottomMenuVisible: boolean = false;
    public isTermsVisible = false;
    public globalOverlayVisibility: boolean = false;
    public globalOverlayWasOpenedLocally: boolean|null = null;
    public globalOverlayComponent: string = null;
    public formVisible: boolean = false;
    public currentForm: string = '';
    public currentItemId: number = null;
    public changePasswordDialogVisible: boolean = false;
    public setPinDialogVisible: boolean = false;
    public errorMessages: ErrorResponse[] = [];
    public bottomNavigationCloseButton: string = '';
    public addButtonAction: string = '';

    @Mutation
    public RESET_VIEWPORT_STORE_STATE(): void {
        this.isRequireAutoHeight           = false;
        this.width                         = 0;
        this.height                        = 0;
        this.deviceWidth                   = 0;
        this.deviceHeight                  = 0;
        this.isMobile                      = false;
        this.isDesktop                     = false;
        this.isTablet                      = false;
        this.isLandscape                   = false;
        this.initalized                    = false;
        this.isShelfVisible                = false;
        this.headerTextContent             = '';
        this.rightHeaderButtonKey          = '';
        this.leftHeaderButtonKey           = '';
        this.view                          = '';
        this.viewMode                      = '';
        this.transparentHeader             = false;
        this.animateTransparentHeader      = false;
        this.searchFieldVisible            = false;
        this.deviceInfo                    = null;
        this.bottomMenuVisible             = false;
        this.isTermsVisible                = false;
        this.errorMessages                 = [];
        this.globalOverlayVisibility       = false;
        this.globalOverlayWasOpenedLocally = null;
        this.globalOverlayComponent        = null;
        this.formVisible                   = false;
        this.currentForm                   = '';
        this.currentItemId                 = null;
        this.changePasswordDialogVisible   = false;
        this.setPinDialogVisible           = false;
        this.errorMessages                 = [];
        this.bottomNavigationCloseButton   = '';
        this.addButtonAction               = '';
    }

    get getIsRequireAutoHeight(): boolean {
        return this.isRequireAutoHeight;
    }

    get getBottomNavigationCloseButton(): string {
        return this.bottomNavigationCloseButton;
    }

    get termsVisible(): boolean {
        return this.isTermsVisible;
    }

    get menuVisible(): boolean {
        return this.formVisible;
    }

    get isSearchFieldVisible(): boolean {
        return this.searchFieldVisible;
    }

    get mobileLayout(): boolean {
        return this.isMobile;
    }

    get tabletLayout(): boolean {
        return this.isTablet;
    }

    get desktopLayout(): boolean {
        return this.isDesktop;
    }

    get shelfVisible(): boolean {
        return this.isShelfVisible;
    }

    get headerText(): string {
        return this.headerTextContent;
    }

    get leftHeaderButton(): string {
        return this.leftHeaderButtonKey;
    }

    get rightHeaderButton(): string {
        return this.rightHeaderButtonKey;
    }

    get getCurrentView(): ViewOptions {
        return {
            view: this.view,
            viewMode: this.viewMode,
        };
    }

    get hasTransparentHeader(): boolean {
        return this.transparentHeader;
    }

    get shouldAnimateHeaderTransition(): boolean {
        return this.animateTransparentHeader;
    }

    get viewportSize(): object {
        return {
            width: this.deviceWidth,
            height: this.deviceHeight,
        };
    }

    get isAddFormVisible() {
        return (formKey: string) => {
            return formKey === this.currentForm;
        };
    }

    get isEditFormVisible() {
        return (formKey: string, itemId: any) => {
            return formKey === this.currentForm && itemId === this.currentItemId;
        };
    }

    get errorDialogVisible() {
        return (this.errorMessages.length > 0);
    }

    get currentErrorMessage() {
        if (this.errorMessages.length === 0) {
            return null;
        }

        return this.errorMessages[0].message;
    }

    get changePasswordVisible() {
        return this.changePasswordDialogVisible;
    }

    get setPinVisible() {
        return this.setPinDialogVisible;
    }

    get getGlobalOverlayVisibility(): boolean {
        return this.globalOverlayVisibility;
    }

    get getGlobalOverlayComponent(): string|null {
        return this.globalOverlayComponent;
    }

    get getGlobalOverlayWasOpenedLocally(): boolean|null {
        return this.globalOverlayWasOpenedLocally;
    }

    get getAddButtonAction(): string {
        return this.addButtonAction;
    }

    @Action
    public SHOW_FORM(formKey: string): void {
        store.commit(Mutations.SET_CURRENT_FORM, formKey);
        store.commit(Mutations.SET_FORM_VISIBILITY, true);
    }

    @Action
    public HIDE_FORM(): void {
        store.commit(Mutations.SET_FORM_VISIBILITY, false);
    }

    @Action
    public INITIALIZE_VIEWPORT(): void {
        if (!Viewport.state.initialized) {
            Breakpoints({
                mobile: {
                    min: 0,
                    max: 767,
                },
                tablet: {
                    min: 768,
                    max: 991,
                },
                desktop: {
                    min: 992,
                    max: Infinity,
                },
            });

            window.addEventListener('resize', () => { store.commit(Mutations.SET_VIEWPORT_DETAILS); }, false);
            Viewport.state.initialized = true;
        }

        store.commit(Mutations.SET_VIEWPORT_DETAILS);
    }

    @Mutation
    public TOGGLE_SEARCH_FIELD(toggle: boolean) {
        this.searchFieldVisible = toggle;
    }

    @Mutation
    public SET_IS_REQUIRE_AUTO_HEIGHT(isRequire: boolean): void {
        this.isRequireAutoHeight = isRequire;
    }

    @Mutation
    public SET_VIEWPORT_DETAILS(): void {
        this.width = window.document.documentElement.clientWidth;
        this.height = window.document.documentElement.clientHeight;
        this.deviceWidth = window.screen.width;
        this.deviceHeight = window.screen.height;
        this.isLandscape = (this.width >= this.height);

        // Check for mobile by user agent
        this.isMobile = Breakpoints.is('mobile');
        this.isTablet = Breakpoints.is('tablet');
        this.isDesktop = Breakpoints.is('desktop');
    }

    @Mutation
    public SET_VIEW(viewOptions: ViewOptions) {
        this.view = viewOptions.view;
        this.viewMode = (viewOptions.viewMode !== undefined) ? viewOptions.viewMode : '';
    }

    @Mutation
    public SET_VIEW_MODE(viewMode: string) {
        this.viewMode = viewMode;
    }

    @Mutation
    public SET_SHELF_VISIBILITY(visibility: boolean) {
        this.isShelfVisible = visibility;
    }

    @Mutation
    public SET_HEADER_TEXT(title: string) {
        this.headerTextContent = title;
    }

    @Mutation
    public SET_RIGHT_HEADER_BUTTON(buttonKey: string) {
        this.rightHeaderButtonKey = buttonKey;
    }

    @Mutation
    public SET_LEFT_HEADER_BUTTON(buttonKey: string) {
        this.leftHeaderButtonKey = buttonKey;
    }

    @Mutation
    public SET_DEVICE_INFO(deviceInfo: any) {
        this.deviceInfo = deviceInfo;
    }

    @Action
    public SHOW_SHELF(): void {
        store.commit(Mutations.SET_SHELF_VISIBILITY, true);
    }

    @Action
    public HIDE_SHELF(): void {
        store.commit(Mutations.SET_SHELF_VISIBILITY, false);
    }

    @Mutation
    public SET_TRANSPARENT_HEADER(): void {
        this.transparentHeader = true;
    }

    @Mutation
    public SET_OPAQUE_HEADER(): void {
        this.transparentHeader = false;
    }

    @Mutation
    public ANIMATE_TRANSPARENT_HEADER(): void {
        this.animateTransparentHeader = true;
    }

    @Mutation
    public SET_FORM_VISIBILITY(visibility: boolean) {
        this.formVisible = visibility;
        if (!visibility) {
            this.currentForm = '';
        }
    }

    @Mutation
    public SET_CURRENT_FORM(formKey: string) {
        this.currentForm = formKey;
    }

    @Mutation
    public SET_CURRENT_ITEM_ID(itemId: number | null) {
        this.currentItemId = itemId;
    }

    @Mutation
    public ADD_ERROR_RESPONSE(responseData: ErrorResponse) {
        this.errorMessages.push(responseData);
    }

    @Mutation
    public DISMISS_CURRENT_ERROR_MESSAGE() {
        this.errorMessages.shift();
    }

    @Mutation
    public SET_BOTTOM_MENU_VISIBILITY(visible: boolean) {
        this.bottomMenuVisible = visible;
    }

    @Mutation
    public SHOW_TERMS() {
        this.isTermsVisible = true;
    }

    @Mutation
    public DISMISS_TERMS() {
        this.isTermsVisible = false;
    }

    @Mutation
    public SHOW_CHANGE_PASSWORD(visible: boolean = true) {
        this.changePasswordDialogVisible = visible;
    }

    @Mutation
    public SHOW_SET_PIN(visible: boolean = true) {
        this.setPinDialogVisible = visible;
    }

    @Mutation
    public SET_BOTTOM_NAVIGATION_CLOSE_BUTTON(buttonAction: string) {
        this.bottomNavigationCloseButton = buttonAction;
    }

    @Mutation
    public SET_GLOBAL_OVERLAY_COMPONENT(component: string) {
        this.globalOverlayComponent = component;
    }

    @Mutation
    public SET_GLOBAL_OVERLAY_VISIBILITY(visibility: boolean) {
        this.globalOverlayVisibility = visibility;
    }

    @Mutation
    public SET_GLOBAL_OVERLAY_WAS_OPENED_LOCALLY(locality: boolean|null) {
        this.globalOverlayWasOpenedLocally = locality;
    }

    @Mutation
    public SET_ADD_ACTION_BUTTON(action: string) {
        this.addButtonAction = action;
    }
}
